import { atom } from 'jotai';
import { atomWithObservable } from 'jotai/utils';
import { from } from 'rxjs';
import { TokenService } from '@/services/auth/tokenService';
import { createApiClient } from '@/services/api/httpClient';
import { createLongPollingObservable } from '@/utils/longPolling';
import { ClientsResponse, Client } from '@/types/api/clients';
import { ClientSearchParams } from '@/types/api/clientSearch';

const fallbackClientsResponse: ClientsResponse = {
  clients: [],
};

export const clientsAtom = atom(async () => {
  const tokenService = TokenService.getInstance();
  const authToken = await tokenService.getCurrentToken();

  if (!authToken) {
    throw new Error('Authentication required');
  }

  const apiClient = createApiClient(() => authToken);
  return apiClient.get<ClientsResponse>('/clients?action=search&client_name=');
});

// Observable-based atom for real-time updates with long polling
export const clientsObservableAtom = atomWithObservable(() => {
  const tokenService = TokenService.getInstance();
  const storedTokens = tokenService.getStoredTokens();

  if (!storedTokens || tokenService.isTokenExpired()) {
    return from(Promise.resolve(fallbackClientsResponse));
  }

  return createLongPollingObservable(
    '/clients?action=search&client_name=',
    storedTokens.idToken,
    60000,
    fallbackClientsResponse,
  );
});

// Search params atom
export const clientSearchParamsAtom = atom<ClientSearchParams>({
  client_name: '',
  property_address: '',
  property_state: '',
});

// Filtered clients atom using search endpoint
export const filteredClientsAtom = atom(async (get) => {
  const tokenService = TokenService.getInstance();
  const authToken = await tokenService.getCurrentToken();
  const searchParams = get(clientSearchParamsAtom);

  if (!authToken) {
    throw new Error('Authentication required');
  }

  // Build query string
  const queryParams = new URLSearchParams({
    action: 'search',
    ...searchParams,
  });

  const apiClient = createApiClient(() => authToken);
  return apiClient.get<ClientsResponse>(`/clients?${queryParams.toString()}`);
});

// Selected client ID atom
export const selectedClientIdAtom = atom<string | null>(null);

// Selected client derived atom
export const selectedClientAtom = atom(async (get) => {
  const selectedClientId = get(selectedClientIdAtom);
  const clientsResponse = await get(clientsAtom);

  if (!selectedClientId || !clientsResponse?.clients) {
    return null;
  }

  return (
    clientsResponse.clients.find(
      (client: Client) => client.client_id === selectedClientId,
    ) || null
  );
});
